// Captures the original Error out-of-band so server.ts can recover the stack
// when h3 has already swallowed the throw into a generic 500 Response.

let lastCapturedError: { error: unknown; at: number } | undefined;
const TTL_MS = 5_000;

/**
 * Client disconnects (tab close, navigation, HMR refresh) abort the in-flight
 * request, and the Node adapter surfaces that as an AbortError. It is expected
 * control flow, so it must never be recorded — otherwise the next 500 gets
 * blamed on it and the user sees a branded crash page.
 */
function isAbort(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") return true;
  return error instanceof Error && error.name === "AbortError";
}

function record(error: unknown) {
  if (isAbort(error)) return;
  lastCapturedError = { error, at: Date.now() };
}


const CHUNK_ERROR_PATTERNS = [
  "importing a module script failed",
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "load failed",
  "dynamically imported module",
  // A tab left open across a rebuild calls server-function IDs the new build
  // no longer knows, which comes back as an HTML 500 page. Same cure as a
  // stale chunk: reload once so the client picks up the current build.
  "invalid server function id",
];


function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const anyErr = err as { name?: string; message?: string };
  if (anyErr.name === "ChunkLoadError") return true;
  const msg = (anyErr.message ?? String(err)).toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => msg.includes(p));
}

const RELOAD_KEY = "__chunk_reload_at";
const RELOAD_WINDOW_MS = 10_000;

function maybeReloadForChunkError(err: unknown) {
  if (typeof window === "undefined") return;
  if (!isChunkLoadError(err)) return;
  try {
    const prev = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
    const now = Date.now();
    if (prev && now - prev < RELOAD_WINDOW_MS) return; // already reloaded recently
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
    window.location.reload();
  } catch {
    // sessionStorage unavailable — fall through, error UI will render
  }
}

if (typeof globalThis.addEventListener === "function") {
  globalThis.addEventListener("error", (event) => {
    const err = (event as ErrorEvent).error ?? event;
    record(err);
    maybeReloadForChunkError(err);
  });
  globalThis.addEventListener("unhandledrejection", (event) => {
    const err = (event as PromiseRejectionEvent).reason;
    record(err);
    maybeReloadForChunkError(err);
  });
}

export function consumeLastCapturedError(): unknown {
  if (!lastCapturedError) return undefined;
  if (Date.now() - lastCapturedError.at > TTL_MS) {
    lastCapturedError = undefined;
    return undefined;
  }
  const { error } = lastCapturedError;
  lastCapturedError = undefined;
  return error;
}

export { isChunkLoadError };
